import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  Input,
  Self,
} from '@angular/core';
import {
  ControlEvent,
  ControlValueAccessor,
  FormControl,
  NgControl,
  TouchedChangeEvent,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { SelectOption } from '../model/select-option';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, filter, map } from 'rxjs';
import { faCircleInfo, faXmark } from '@fortawesome/free-solid-svg-icons';

interface ControlData {
  isTouched: boolean;
  isRequired: boolean;
  errors: ValidationErrors | null;
}

@Component({
  selector: 'canvas-select',
  templateUrl: './select.component.html',
  styleUrl: './select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SelectComponent implements ControlValueAccessor, AfterContentInit {
  @Input() removeExclamationIcon = true;
  @Input() hideExclamationIcon = false;
  @Input() tooltip: string | null = null;
  @Input() clearValueEnabled = false;
  @Input() label = '';
  @Input() nullText = '';
  @Input() options: SelectOption[] = [];

  selectControl = new FormControl(null);
  searchControl = new FormControl<string>('');
  filteredOptions: SelectOption[] = [];
  faXmark = faXmark;

  private onTouched = () => {};
  private onChanged = (value: any): void => {};
  private destroyRef = inject(DestroyRef);

  dropdownOpen = false;

  private controlDataSubject = new BehaviorSubject<ControlData>({
    isRequired: false,
    isTouched: false,
    errors: null,
  });

  data$ = this.controlDataSubject.asObservable();

  faCircleInfo = faCircleInfo;
  constructor(@Self() private ngControl: NgControl, private elRef: ElementRef) {
    ngControl.valueAccessor = this;

    this.selectControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value) => this.onChanged(value));

    this.selectControl.events
      .pipe(
        filter((event) => event instanceof TouchedChangeEvent),
        filter((x) => x.touched),
        takeUntilDestroyed()
      )
      .subscribe(() => this.onTouched());

    this.searchControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((search) => {
        const term = (search ?? '').toLowerCase();
        this.filteredOptions = this.options.filter((o) =>
          o.text.toLowerCase().includes(term)
        );
      });
  }

  ngAfterContentInit(): void {
    this.filteredOptions = this.options;

    const control = this.ngControl?.control;

    if (control) {
      this.controlDataSubject.next({
        ...this.controlDataSubject.value,
        isRequired: control.hasValidator(Validators.required),
      });

      control.events
        .pipe(
          map<ControlEvent, ControlData>((event) => {
            let { isTouched } = this.controlDataSubject.value;

            if (event instanceof TouchedChangeEvent) {
              isTouched = event.touched;
            }

            return {
              isRequired: control.hasValidator(Validators.required),
              isTouched,
              errors: control.errors,
            };
          }),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe((data) => this.controlDataSubject.next(data));
    }
  }
  writeValue(value: any) {
    this.selectControl.setValue(value);
  }

  registerOnChange(onChanged: (value: any) => void) {
    this.onChanged = onChanged;
  }

  registerOnTouched(onTouched: () => void) {
    this.onTouched = onTouched;
  }

  setDisabledState(disabled: boolean) {
    disabled ? this.selectControl.disable() : this.selectControl.enable();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const clickedInside = this.elRef.nativeElement.contains(event.target);
    if (!clickedInside && this.dropdownOpen) {
      this.dropdownOpen = false;
    }
  }

  toggleDropdown() {
    if (this.selectControl.disabled) {
      return;
    }

    this.dropdownOpen = !this.dropdownOpen;
    if (this.dropdownOpen) {
      this.filteredOptions = this.options;
      this.searchControl.setValue('');
    }
  }

  selectOption(option: SelectOption) {
    this.selectControl.setValue(option.id);
    this.selectControl.markAsTouched();
    this.dropdownOpen = false;
  }

  getSelectedText(): string {
    const selectedId = this.selectControl.value;
    const selectedOption = this.options?.find((o) => o.id === selectedId);
    return selectedOption?.text ?? '';
  }

  clearSelection() {
    this.selectControl.setValue(null);
    this.selectControl.markAsTouched();
    this.dropdownOpen = false;
  }
}
